import * as React from 'react';
import { AlertTriangleIcon, InboxIcon, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';

export interface ResultStateProps {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Buttons under the text. */
  action?: React.ReactNode;
  tone?: 'default' | 'warning' | 'destructive' | 'success';
  className?: string;
}

const TONE: Record<NonNullable<ResultStateProps['tone']>, string> = {
  default: 'bg-muted text-muted-foreground',
  warning: 'bg-warning/12 text-warning',
  destructive: 'bg-destructive/12 text-destructive',
  success: 'bg-success/12 text-success',
};

/**
 * A centred icon, title, description and action — the shape shared by every
 * empty, error, not-found and "done" state. The replacement for antd's
 * `<Result>` and `<Empty>`.
 */
export function ResultState({
  icon,
  title,
  description,
  action,
  tone = 'default',
  className,
}: ResultStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-10 text-center',
        className,
      )}
    >
      {icon ? (
        <div
          className={cn(
            'flex size-11 items-center justify-center rounded-full [&_svg]:size-5',
            TONE[tone],
          )}
        >
          {icon}
        </div>
      ) : null}
      {title || description ? (
        <div className="grid max-w-md gap-1">
          {title ? <p className="text-[15px] font-semibold">{title}</p> : null}
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
      ) : null}
      {action ? (
        <div className="flex flex-wrap items-center justify-center gap-2">{action}</div>
      ) : null}
    </div>
  );
}

export interface ErrorStateProps {
  title?: React.ReactNode;
  message?: React.ReactNode;
  onRetry?: () => void;
  /** Rendered instead of the retry button — e.g. "Back to list". */
  extra?: React.ReactNode;
  className?: string;
}

/**
 * Shown when a query fails.
 *
 * Every list and detail page must render one of these on error. Silently
 * showing an empty table when the request 500s is the single most common way an
 * admin app lies to its users.
 */
export function ErrorState({ title, message, onRetry, extra, className }: ErrorStateProps) {
  return (
    <ResultState
      tone="warning"
      icon={<AlertTriangleIcon />}
      title={title ?? "That didn't load"}
      description={message}
      className={className}
      action={
        extra ??
        (onRetry ? (
          <Button size="sm" onClick={onRetry}>
            Try again
          </Button>
        ) : null)
      }
    />
  );
}

export interface EmptyStateProps {
  description?: React.ReactNode;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  /** The call to action that resolves the emptiness — usually "New X". */
  action?: React.ReactNode;
  className?: string;
}

export function EmptyState({ description, title, icon, action, className }: EmptyStateProps) {
  return (
    <ResultState
      icon={icon ?? <InboxIcon />}
      title={title}
      description={description ?? 'Nothing here yet'}
      action={action}
      className={cn('py-8', className)}
    />
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-5 animate-spin text-muted-foreground', className)} />;
}

export interface PageLoaderProps {
  tip?: string;
  /** Fill the viewport rather than the content area — for the initial app boot. */
  fullscreen?: boolean;
}

export function PageLoader({ tip, fullscreen = false }: PageLoaderProps) {
  return (
    <div
      className={cn(
        'flex w-full flex-col items-center justify-center gap-3',
        fullscreen ? 'min-h-screen' : 'min-h-60',
      )}
    >
      <Spinner className="size-6" />
      {tip ? <p className="text-sm text-muted-foreground">{tip}</p> : null}
    </div>
  );
}
